/**
 * Copyright © 2026 Adnuntius AS.
 */
import {SignalResourceListBindingService} from '../common/controller/signal-resource-list-binding.service';
import {AppLocaleService} from '../common/locale/app-locale.service';
import {compareById} from '../common/util/object-id-comparison';
import {DestroyRef, inject, DOCUMENT, Component, DoCheck, Inject, Input, OnDestroy, signal, WritableSignal} from '@angular/core';
import {TranslateService} from '@ngx-translate/core';
import {NgForm} from '@angular/forms';
import * as _ from 'lodash';
import template from './line-item-edit-page.component.html';
import {ADVERTISING_SHARED} from '../advertising/advertising-shared';
import {LineItemCreativeListComponent} from './line-item-creative-list.component';
import {SignalAdminResourceEditPageBase} from '../common/controller/signal-admin-resource-edit-page-base';
import {LineItemCpmFeesModalService} from './line-item-cpm-fees-modal.service';
import {CreativeFromLineItemCopyModalService} from './creative-from-line-item-copy-modal.service';
import {MpProductModalService} from './mp-product-modal.service';
import {OrderBudgetModalService} from '../order/order-budget-modal.service';
import {CreativePreviewModalService} from '../creative/creative-preview-modal.service';
import {LineItemRetailProductModalService} from './line-item-retail-product-modal.service';
import {CreativeBulkActionService} from '../creative/creative-bulk-action.service';
import {BurnRatesApiService} from './burn-rates-api.service';
import {StatsApiService} from '../common/controller/stats-api.service';
import {CreativeCopyInBulkModalService} from '../creative/creative-copy-in-bulk-modal.service';
import {TargetingEditorModalService} from '../targeting/targeting-editor-modal.service';
import {LineItemEditBehaviorService} from './line-item-edit-setup';
import {IAB_TAXONOMY} from '../../components/util/iab-taxonomy';
import {isIabCategoryEnabledForNetwork} from './iab-category-network';
import {AdnLogService} from '../../components/util/adn-log.service';
import {OrderApiService} from '../order/order-api.service';
import {TeamApiService} from '../admin/team/team-api.service';
import {AdvertiserApiService} from '../advertiser/advertiser-api.service';
import {CreativeApiService} from '../creative/creative-api.service';
import {MarketplaceProductApiService} from '../admin/marketplace-product/marketplace-product-api.service';
import {NetworkDataApiService} from '../admin/network/network-data-api.service';
import {PublicPoliticalCampaignApiService} from '../political-campaign/public-political-campaign-api.service';
import {AdnInactiveTargetsService} from '../../components/util/adn-inactive-targets.service';
import {AdnModalLoadingService} from '../common/util/adn-modal-loading.service';
import {AdnRollbarService} from '../../components/util/adn-rollbar.service';
import {AdnTimeShifterService} from '../../components/util/adn-time-shifter.service';
import {DataViewApiService} from '../common/controller/data-view-api.service';
import {LocalNetworkProfile} from '../../components/session/local-network-profile';
import {LocalUserPermissions} from '../../components/session/local-user-permissions';
import {LocalUserProfile} from '../../components/session/local-user-profile';
import {LocalNetworkInfo} from '../../components/session/local-network-info';
import {BrowserLocationService} from '../angular-platform-providers';
import {ADN_STAT_FILTER} from '../common/filters/adn-stat-filter';
import {StateService} from '@uirouter/core';
import {CustomEventApiService} from '../admin/custom-event/custom-event-api.service';
import {LineItemApiService, LineItemEditApi, LineItemModel} from './line-item-api.service';
import {lineItemEditApiForModel, LineItemTemplateApiService} from './line-item-template-api.service';
import {Observable} from 'rxjs';
import {FormSubmitState} from '../common/controller/form-submit-state';

@Component({
  standalone: true,
  imports: [...ADVERTISING_SHARED, LineItemCreativeListComponent],
  selector: 'adn-line-item-edit-page',
  template
})
export class LineItemEditPageComponent extends SignalAdminResourceEditPageBase<LineItemModel> implements DoCheck, OnDestroy {
  private readonly listLifetime = inject(DestroyRef);
  [key: string]: any;

  private _fullDirect: boolean | undefined;
  private _viewTypeDirect: number | undefined;
  private _orderFromParamDirect: any;
  private _advertiserFromParamDirect: any;
  private _lineItemViewDirect: any;
  private setupInitialized = false;
  private destroyCallbacks: Array<() => void> = [];
  private activeForm: NgForm | null = null;
  private lineItemInitialized = false;
  private readonly dirtyTrackingReady = signal(false);
  private userEditDuringDirtyTrackingInit = false;
  private dirtyTrackingResumeTimer: ReturnType<typeof setTimeout> | null = null;
  private dirtyTrackingFallbackTimer: ReturnType<typeof setTimeout> | null = null;
  private readonly postSaveHydrationPending = signal(false);
  private postSaveHydrationTimer: ReturnType<typeof setTimeout> | null = null;
  private readonly marketplaceResolvedDisplayState = signal(false);
  private marketplaceResolvedDisplayTimer: ReturnType<typeof setTimeout> | null = null;

  /** Eager copy so the IAB <select> options exist before first CD (avoids clearing ngModel). */
  iabTaxonomy: any[] = _.cloneDeep(IAB_TAXONOMY);
  uiFees: any = {fees: []};
  showIabCategory = false;
  showIabCategoryOnNetwork = false;

  /** Async / visibility view state consumed directly by the template. */
  readonly marketplaceResolved = signal(false);
  readonly teams = signal<any[]>([]);
  readonly customEvents = signal<any[] | undefined>(undefined);
  readonly showPublisherApprovals = signal(false);
  readonly publisherApprovals = signal<any[]>([]);
  readonly publisherApprovalsLoaded = signal(false);
  readonly publisherApprovalsLoading = signal(false);
  readonly publisherApprovalsError = signal(false);
  readonly showBidStrategy = signal(false);
  readonly showVCpmStrategy = signal(false);
  readonly showDynamicCpmStrategy = signal(false);
  readonly showFloorBidStrategy = signal(false);
  readonly rateLimitReadOnly = signal(false);
  readonly isLockedDown = signal(false);
  readonly showObjectives = signal(false);
  readonly singleObjective = signal(false);
  readonly singleBudgetObjective = signal(false);
  readonly currentBurnRate = signal<number | undefined>(undefined);
  readonly hasStats = signal(false);

  /**
   * Phased init query for adn-promise-spinner. Stays {$resolved:false} through
   * seeding/hydrating so marketplace-dependent sections are out of CD; replaced once at ready.
   * `editPhase` / `editPhaseApi` are attached by LineItemEditBehaviorService.
   */
  editPhaseQuery: {$resolved: boolean} = {$resolved: false};
  editPhase: any;
  editPhaseApi: any;

  /**
   * Buffers !isAxAdvertiser || !!axProduct — marketplace hydrate assigns axProduct
   * mid-CD (false→true NG0100 on the delivery/objectives section *ngIf).
   */
  private readonly showMainSectionsState = signal(false);

  /**
   * objectivesAsString() rebuilds from live objectives + network translations.
   * Product hydrate / profile translations can flip
   * "780 000 impressions" → "780 000 rendered impressions" mid-check (NG0100).
   */
  private readonly objectivesAsStringState = signal('');

  /**
   * Marketplace hydrate replaces axProduct with the catalog copy mid-CD
   * ("Gold Banner…" → "Category Product List…") on the product name span.
   */
  private readonly axProductNameState = signal('');
  private readonly productLabelState = signal('');
  private readonly hasMarketplaceProductContextState = signal(false);
  private readonly smoothingOptionsState = signal<string[]>([]);
  private readonly showDateRangeState = signal(false);
  private readonly showBudgetDateRangeState = signal(false);

  /** The base signal effect can install the full wizard model during its first CD turn. */
  private readonly wizardSaveDisabledState = signal(true);
  private wizardForm: NgForm | null = null;

  smoothingOptions: string[] = [];

  get displayShowMainSections(): boolean {
    return this.showMainSectionsState();
  }

  get displayObjectivesAsString(): string {
    return this.objectivesAsStringState();
  }

  get displayAxProductName(): string {
    return this.axProductNameState();
  }

  get displayProductLabel(): string {
    return this.productLabelState();
  }

  get displayHasMarketplaceProductContext(): boolean {
    return this.hasMarketplaceProductContextState();
  }

  get displaySmoothingOptions(): string[] {
    return this.smoothingOptionsState();
  }

  get displayShowDateRange(): boolean {
    return this.showDateRangeState();
  }

  get displayShowBudgetDateRange(): boolean {
    return this.showBudgetDateRangeState();
  }

  /**
   * Order selection can start marketplace loading from the search-select child
   * after this parent view has already been checked. Keep the spinner binding
   * stable for that verification pass, then publish the new state next turn.
   */
  get displayMarketplaceResolved(): boolean {
    const display = this.marketplaceResolvedDisplayState();
    const resolved = this.marketplaceResolved();
    if (display !== resolved && !this.marketplaceResolvedDisplayTimer) {
      this.marketplaceResolvedDisplayTimer = setTimeout(() => {
        this.marketplaceResolvedDisplayTimer = null;
        if (!this.listLifetime.destroyed) {
          this.marketplaceResolvedDisplayState.set(this.marketplaceResolved());
        }
      }, 0);
    }
    return display;
  }

  displayWizardSaveDisabled(wizardForm: NgForm): boolean {
    this.wizardForm = wizardForm;
    return this.wizardSaveDisabledState();
  }

  ngDoCheck(): void {
    const model = this.editingModel;
    this.syncProductViewState();
    this.setIfChanged(this.objectivesAsStringState, typeof this.objectivesAsString === 'function' ? this.objectivesAsString() : '');
    this.setIfChanged(this.smoothingOptionsState, this.smoothingOptions);
    this.setIfChanged(this.showDateRangeState, !this.isLiTemplate && !(model?.budget?.length > 0));
    this.setIfChanged(this.showBudgetDateRangeState, !this.isLiTemplate && !!(model?.budget?.length > 0));
    const wizardForm = this.wizardForm;
    this.setIfChanged(this.wizardSaveDisabledState, !(model?.name || '').trim()
      || !model?.order
      || this.pending
      || !!(wizardForm?.invalid && !this.hasValidationError));
  }

  syncProductViewState(): void {
    const model = this.editingModel;
    this.setIfChanged(this.showMainSectionsState, !this.isAxAdvertiser || !!model?.axProduct);
    this.setIfChanged(this.axProductNameState, model?.axProduct?.name || '');
    this.setIfChanged(this.productLabelState, this.productString || model?.axProduct?.name || 'No type has been selected');
    this.syncMarketplaceProductContext();
  }

  syncMarketplaceProductContext(): void {
    const model = this.editingModel;
    this.setIfChanged(this.hasMarketplaceProductContextState, !!model?.axProduct || model?.team?.type === 'AX_ADVERTISER');
  }

  private setIfChanged<T>(state: WritableSignal<T>, value: T): void {
    if (!Object.is(state(), value)) {
      state.set(value);
    }
  }

  @Input()
  set full(value: boolean) {
    this._fullDirect = value;
  }

  get full(): boolean {
    return this._fullDirect;
  }

  @Input()
  set viewType(value: number) {
    this._viewTypeDirect = value;
  }

  get viewType(): number {
    return this._viewTypeDirect;
  }

  @Input()
  set orderFromParam(value: any) {
    this._orderFromParamDirect = value;
  }

  get orderFromParam(): any {
    return this._orderFromParamDirect;
  }

  @Input()
  set lineItemView(value: any) {
    this._lineItemViewDirect = value;
  }

  get lineItemView(): any {
    return this._lineItemViewDirect;
  }

  @Input()
  set advertiserFromParam(value: any) {
    this._advertiserFromParamDirect = value;
  }

  get advertiserFromParam(): any {
    return this._advertiserFromParamDirect;
  }

  constructor(
    @Inject(LocalNetworkProfile) private readonly lineItemNetworkProfile: any,
    private readonly $state: StateService,
    @Inject(BrowserLocationService) private readonly location: any,
    private readonly adnLog: AdnLogService,
    @Inject(DOCUMENT) private readonly document: any,
    private readonly appLocale: AppLocaleService,
    private readonly translate: TranslateService,
    @Inject(AdnInactiveTargetsService) private readonly adnInactiveTargets: any,
    private readonly lineItemApi: LineItemApiService,
    private readonly lineItemTemplateApi: LineItemTemplateApiService,
    private readonly advertiserApi: AdvertiserApiService,
    private readonly publicPoliticalCampaignApi: PublicPoliticalCampaignApiService,
    @Inject(LocalUserProfile) private readonly localUserProfile: any,
    private readonly burnRatesApi: BurnRatesApiService,
    @Inject(NetworkDataApiService) private readonly NetworkData: NetworkDataApiService,
    @Inject(AdnModalLoadingService) private readonly adnModalLoading: any,
    private readonly listBinding: SignalResourceListBindingService,
    private readonly teamApi: TeamApiService,
    @Inject(ADN_STAT_FILTER) private readonly adnStatFilter: any,
    @Inject(AdnRollbarService) private readonly Rollbar: any,
    private readonly orderApi: OrderApiService,
    @Inject(MarketplaceProductApiService) private readonly MarketplaceProduct: MarketplaceProductApiService,
    @Inject(LocalUserPermissions) private readonly LocalUserPermissions: any,
    private readonly statsApi: StatsApiService,
    private readonly customEventApi: CustomEventApiService,
    private readonly dataViewApi: DataViewApiService,
    @Inject(CreativeApiService) private readonly Creative: CreativeApiService,
    @Inject(AdnTimeShifterService) private readonly adnTimeShifter: any,
    private readonly targetingEditorModalService: TargetingEditorModalService,
    private readonly lineItemCpmFeesModalService: LineItemCpmFeesModalService,
    private readonly creativeFromLineItemCopyModalService: CreativeFromLineItemCopyModalService,
    private readonly mpProductModalService: MpProductModalService,
    private readonly orderBudgetModalService: OrderBudgetModalService,
    private readonly creativePreviewModalService: CreativePreviewModalService,
    private readonly lineItemRetailProductModalService: LineItemRetailProductModalService,
    private readonly creativeBulkActionService: CreativeBulkActionService,
    private readonly creativeCopyInBulkModalService: CreativeCopyInBulkModalService,
    private readonly lineItemEditBehaviorService: LineItemEditBehaviorService,
    private readonly localNetworkInfo: LocalNetworkInfo = new LocalNetworkInfo()
  ) {
    super(lineItemApi, 'lineItem');
    this.showIabCategoryOnNetwork = isIabCategoryEnabledForNetwork(this.lineItemNetworkProfile.getNetworkTag());
    this.showIabCategory = this.lineItemNetworkProfile.getDefaults().iabCategoryOnLineItem === 'SHOW';
  }

  private get editApi(): LineItemEditApi {
    return lineItemEditApiForModel(this.state?.model() || this.editingModel, this.lineItemApi, this.lineItemTemplateApi);
  }

  get creativeListHost(): this {
    return this;
  }

  readonly compareById = compareById;

  get formSubmitState(): FormSubmitState {
    return this.submitState;
  }

  protected override initialiseModel(model: LineItemModel): LineItemModel {
    const initialised = super.initialiseModel(model);
    const bidSpecification = _.isPlainObject(initialised.bidSpecification)
      ? {...initialised.bidSpecification}
      : {};
    const cpa = _.isPlainObject(bidSpecification.cpa) ? {...bidSpecification.cpa} : {};
    const optimiseFor = _.isPlainObject(bidSpecification.optimiseFor)
      ? {...bidSpecification.optimiseFor}
      : {eventType: 'IMPRESSION'};

    return {
      ...initialised,
      objectives: initialised.objectives || {},
      targeting: initialised.targeting || {},
      bidSpecification: {
        ...bidSpecification,
        bidStrategy: bidSpecification.bidStrategy || null,
        cpmCostModel: bidSpecification.cpmCostModel || null,
        cpa: {
          ...cpa,
          cost: cpa.cost || {},
          initialECpm: cpa.initialECpm || {}
        },
        optimiseFor: {
          ...optimiseFor,
          eventType: optimiseFor.eventType || 'IMPRESSION'
        }
      }
    };
  }

  ensureCpaStructure(): void {
    if (!this.editingModel) {
      return;
    }
    this.editingModel.bidSpecification = this.editingModel.bidSpecification || {};
    this.editingModel.bidSpecification.cpa = this.editingModel.bidSpecification.cpa || {};
    this.editingModel.bidSpecification.cpa.cost = this.editingModel.bidSpecification.cpa.cost || {};
    this.editingModel.bidSpecification.cpa.initialECpm = this.editingModel.bidSpecification.cpa.initialECpm || {};
  }

  onShowCpaChange(show: boolean): void {
    if (show) {
      this.ensureCpaStructure();
    }
    this.showCpa = show;
  }

  onFieldChange(): void {
    this.markExternalFormDirty();
  }

  onUserFieldInput(): void {
    this.markExternalFormDirty(true);
  }

  editsMade(): void {
    // Match order/advertiser: ignore programmatic calls while init is settling.
    this.markExternalFormDirty();
  }

  override markExternalFormDirty(userInitiated = false): void {
    if ((!this.dirtyTrackingReady() || this.postSaveHydrationPending()) && !userInitiated) {
      return;
    }
    if (!this.dirtyTrackingReady() || this.postSaveHydrationPending()) {
      this.userEditDuringDirtyTrackingInit = true;
    }
    super.markExternalFormDirty();
  }

  protected override isFormDirtyForNavigation(editForm: NgForm): boolean {
    return this.dirtyTrackingReady() && !this.postSaveHydrationPending() && editForm.dirty;
  }

  override isSaveDisabled(editForm: NgForm, additionallyInvalid = false): boolean {
    return !this.dirtyTrackingReady() || super.isSaveDisabled(editForm, additionallyInvalid);
  }

  private beginPostSaveHydration(): void {
    if (this.postSaveHydrationTimer) {
      clearTimeout(this.postSaveHydrationTimer);
      this.postSaveHydrationTimer = null;
    }
    this.postSaveHydrationPending.set(true);
    this.userEditDuringDirtyTrackingInit = false;
  }

  private finishPostSaveHydration(): void {
    this.postSaveHydrationTimer = setTimeout(() => {
      this.postSaveHydrationTimer = null;
      this.postSaveHydrationPending.set(false);
      if (!this.userEditDuringDirtyTrackingInit && this.activeForm) {
        this.activeForm.control.markAsPristine();
        this.activeForm.control.markAsUntouched();
      }
      this.userEditDuringDirtyTrackingInit = false;
    }, 500);
  }

  scheduleResumeFormDirtyTrackingAfterInit(): void {
    if (this.dirtyTrackingReady() || this.dirtyTrackingResumeTimer) {
      return;
    }
    this.dirtyTrackingResumeTimer = setTimeout(() => {
      this.dirtyTrackingResumeTimer = null;
      this.dirtyTrackingReady.set(true);
      if (!this.userEditDuringDirtyTrackingInit && this.activeForm) {
        this.activeForm.control.markAsPristine();
        this.activeForm.control.markAsUntouched();
      }
      this.userEditDuringDirtyTrackingInit = false;
      if (this.dirtyTrackingFallbackTimer) {
        clearTimeout(this.dirtyTrackingFallbackTimer);
        this.dirtyTrackingFallbackTimer = null;
      }
    }, 500);
  }

  protected override afterEditingModelReset(): void {
    this.ensureSetup();
    if (!this.lineItemInitialized) {
      this.lineItemInitialized = true;
      this._lineItemAfterInit?.();
      this.dirtyTrackingFallbackTimer = setTimeout(() => {
        this.dirtyTrackingFallbackTimer = null;
        this.scheduleResumeFormDirtyTrackingAfterInit();
      }, 2500);
    } else {
      // Saving replaces editingModel, then the legacy setup performs deferred
      // product/objective hydration. Treat those writes like initial hydration
      // so they cannot re-dirty the just-saved form and trip the route guard.
      this.beginPostSaveHydration();
      this._lineItemAfterInitAfterSave?.(this.editingModel);
      this.finishPostSaveHydration();
    }
    this._ensureElectionModel?.();
  }

  protected override afterSave(resource: LineItemModel): void {
    if (this.showCpa) {
      this.ensureCpaStructure();
    }
    this._lineItemAfterSave?.(resource);
  }

  protected override prepareForSubmit(_model: LineItemModel): void {
    this.submitState.clientErrors = [];
    this._lineItemOnBeforeSubmit?.();
  }

  protected override persist(payload: LineItemModel): Observable<LineItemModel> {
    const endPoint = this.endPoint || 'save';
    return this.editApi.savePartial(payload, undefined, {}, endPoint === 'save' ? '' : endPoint);
  }

  get submitBlocked(): boolean {
    return !!(this.blockSubmit || this.costFailsafe);
  }

  submit(editForm: NgForm, options?: {skipPristine?: boolean}): void {
    if (!_.trim(this.editingModel?.name || '')) {
      return;
    }
    super.submit(editForm, {
      skipPristine: !!options?.skipPristine,
      additionalInvalid: this.submitBlocked,
      allowInvalidAfterServerValidation: true
    });
  }

  submitWizard(wizardForm: NgForm): void {
    this.onSaveAndNext?.();
    // Wizard Save and Next is always intentional. The main edit form's pristine
    // gate would block here: the wizard form is not dirty-tracked the same way,
    // and product/country thumbnail clicks do not mark the form dirty.
    this.submit(wizardForm, {skipPristine: true});
  }

  registerForm(editForm: NgForm): void {
    super.registerForm(editForm);
    this.activeForm = editForm;
  }

  getForm(): NgForm | null {
    return this.activeForm;
  }

  captureFormDirtyState(): {ngFormDirty: boolean} {
    return {ngFormDirty: !!this.activeForm?.dirty};
  }

  restoreFormDirtyState(state: {ngFormDirty: boolean}): void {
    if (!this.activeForm) {
      return;
    }
    if (state.ngFormDirty) {
      this.activeForm.control.markAsDirty();
    } else {
      this.activeForm.control.markAsPristine();
    }
  }

  ngOnDestroy(): void {
    if (this.dirtyTrackingResumeTimer) {
      clearTimeout(this.dirtyTrackingResumeTimer);
    }
    if (this.dirtyTrackingFallbackTimer) {
      clearTimeout(this.dirtyTrackingFallbackTimer);
    }
    if (this.postSaveHydrationTimer) {
      clearTimeout(this.postSaveHydrationTimer);
    }
    if (this.marketplaceResolvedDisplayTimer) {
      clearTimeout(this.marketplaceResolvedDisplayTimer);
    }
    _.forEach(this.destroyCallbacks, (fn) => fn());
    super.ngOnDestroy();
  }

  publishModelUpdate(): void {
    this.state.syncModel({...this.editingModel});
  }

  private ensureSetup(): void {
    if (this.setupInitialized || !this.model) {
      return;
    }
    this.setupInitialized = true;

    this.lineItemEditBehaviorService.initialize(this, {
      model: this.model,
      readOnly: this.readOnly,
      full: this.full,
      viewType: this.viewType,
      adnInactiveTargets: this.adnInactiveTargets,
      LocalNetworkProfile: this.lineItemNetworkProfile,
      location: this.location,
      lineItemApi: this.editApi,
      Advertiser: this.advertiserApi,
      publicPoliticalCampaignApi: this.publicPoliticalCampaignApi,
      localUserProfile: this.localUserProfile,
      onDestroy: (cleanup) => this.destroyCallbacks.push(cleanup),
      eventBus: this.eventBus,
      burnRatesApi: this.burnRatesApi,
      NetworkData: this.NetworkData,
      adnLog: this.adnLog,
      orderFromParam: this.orderFromParam,
      advertiserFromParam: this.advertiserFromParam,
      adnModalLoading: this.adnModalLoading,
      pageState: this.state,
      listBinding: this.listBinding,
      listLifetime: this.listLifetime,
      teamApi: this.teamApi,
      adnStatFilter: this.adnStatFilter,
      document: this.document,
      Rollbar: this.Rollbar,
      Order: this.orderApi,
      MarketplaceProduct: this.MarketplaceProduct,
      LocalUserPermissions: this.LocalUserPermissions,
      lineItemView: this.lineItemView,
      statsApi: this.statsApi,
      CustomEvent: this.customEventApi,
      dataViewApi: this.dataViewApi,
      appLocale: this.appLocale,
      translate: this.translate,
      Creative: this.Creative,
      adnTimeShifter: this.adnTimeShifter,
      TargetingEditorModalService: this.targetingEditorModalService,
      $state: this.$state,
      lineItemCpmFeesModalService: this.lineItemCpmFeesModalService,
      creativeFromLineItemCopyModalService: this.creativeFromLineItemCopyModalService,
      mpProductModalService: this.mpProductModalService,
      orderBudgetModalService: this.orderBudgetModalService,
      creativePreviewModalService: this.creativePreviewModalService,
      localNetworkInfo: this.localNetworkInfo,
      lineItemRetailProductModalService: this.lineItemRetailProductModalService,
      creativeBulkActionService: this.creativeBulkActionService,
      creativeCopyInBulkModalService: this.creativeCopyInBulkModalService
    });
  }
}
